import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, on, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { ChevronsUpDown, Menu, UserCircle } from "lucide-solid";
import { msg } from "@lingui/core/macro";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { accountsStore, readAccountsStateSync, readActiveAccountSync } from "@/lib/accounts";
import { buildUPath, outOfRangeRedirectIndex, stripUPrefix } from "@/lib/active-account";
import { api, fetchApi } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { Link, useNavigate } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
// F4-A: undo global (Ctrl/Cmd+Z) + limpieza de pila por cuenta.
import { useUndo } from "@/lib/undo";
import { useCommandHandler } from "@/lib/shortcuts/solid";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { ImpersonationBanner } from "@/features/admin/components/impersonation-banner";
import { OfflineBanner } from "@/components/offline-banner";
import { MobileNavDrawer } from "@/features/layout/components/mobile-nav-drawer";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@space/shared";
import { Logo } from "@/components/ui/logo";
// F3-C: campana de notificaciones (contador + dropdown local-first).
import { NotificationsBell } from "@/features/academy/components/community/notifications-bell";
import { validateAppLayoutSearch } from "@/features/layout/app-search";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";
// Navegación por rol/capacidad: modelo declarativo + contexto reactivo.
import { type NavItem, visibleSections } from "@/features/navigation/nav-model";
import { useNavContext } from "@/features/navigation/use-nav-context";
import { RouteHelpLink } from "@/features/help/components/route-help-link";

/**
 * Ranura en la que se monta una copia de la navegación.
 *
 * El shell monta la MISMA navegación DOS veces: la sidebar de escritorio
 * (`hidden md:block`) y el cajón móvil, que desde el cambio de motion está
 * **siempre en el DOM** para poder animar su cierre (un nodo que se desmonta se
 * esfuma en el mismo fotograma). Las dos copias coexisten en el DOM a cualquier
 * ancho: una de ellas solo está oculta por CSS, y `display:none` no la saca del
 * documento.
 *
 * Dos nodos con el MISMO `data-testid` hacen que el modo estricto de Playwright
 * se niegue a elegir («resolved to 2 elements») y **todo** el que navegue por la
 * sidebar se cae. No es un problema del test: un `data-testid` es un
 * identificador y duplicarlo lo deja de identificar nada. Por eso la copia
 * del cajón sufija los suyos, que es la convención que ya tenía
 * `NotificationsBell` con su prop `slot` (ver notifications-bell.tsx).
 */
type NavSlot = "desktop" | "drawer";

const slotTestId = (name: string, slot: NavSlot) => (slot === "drawer" ? `${name}-drawer` : name);

/**
 * Shell de SPACE: navegación por rol/capacidad (nav-model declarativo) + Outlet.
 * Local-first y guest-first — el contenido resuelve offline; el shell se pinta
 * al instante mientras PowerSync inicializa. La visibilidad de cada item
 * reacciona al switch de cuenta/org (contexto reactivo, i18n sin t() congelado).
 */
function SpaceShell() {
  const [mobileOpen, setMobileOpen] = createSignal(false);
  const session = useSession();
  const navigate = useNavigate();
  const { t } = useI18n();
  const navContext = useNavContext();
  const sections = createMemo(() => visibleSections(navContext()));
  const location = useLocation();

  // ── F4-A: undo global (L1) ────────────────────────────────────────────────
  // Ctrl/Cmd+Z deshace la última acción registrada (pila en memoria). La pila
  // se VACÍA al cambiar de cuenta: una inversa jamás debe ejecutarse contra
  // otra sesión (gotcha multi-cuenta de UNDO_TRASH_LIGHT).
  const { undoLast, clearUndoStack } = useUndo();
  useCommandHandler(COMMAND_IDS.UNDO, () => {
    void undoLast();
  });
  createEffect(
    on(
      () => session.userId(),
      () => clearUndoStack(),
      { defer: true },
    ),
  );
  // ── fin F4-A ──────────────────────────────────────────────────────────────

  // URL-addressable switcher (#15b pattern): `?account=switch` opens it. The
  // overlay itself is mounted ONCE at the layout level (AccountSwitcherHost);
  // this trigger only flips the URL flag.
  const openSwitcher = () => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, account: "switch" }),
      replace: true,
    });
  };

  // Reactive view of the active account for the switcher trigger label. Reading
  // `session.data` registers the dependency so this recomputes when the session
  // changes (switch/sign-in re-primes it).
  const activeAccount = createMemo(() => {
    void session.data;
    return readActiveAccountSync();
  });

  const activeLabel = () => {
    const a = activeAccount();
    return a?.kind === "real" ? a.displayName : t(msg`Guest`);
  };

  const activeInitial = () => {
    const a = activeAccount();
    return (a?.displayName ?? a?.email ?? "?").charAt(0);
  };

  // Un item de navegación (estilo/behavior idéntico al Link original).
  const NavLink = (props: { item: NavItem; slot: NavSlot }) => (
    <Link
      to={props.item.to}
      data-testid={slotTestId(props.item.testid, props.slot)}
      onClick={() => setMobileOpen(false)}
      class="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold text-muted-foreground transition-all hover:bg-accent/10 hover:text-accent [&.active]:bg-accent [&.active]:text-accent-foreground [&.active]:shadow-md [&.active]:shadow-accent/25"
      activeProps={{ class: "active" }}
    >
      <span class="transition-transform group-hover:scale-110 group-[.active]:scale-110">
        <Dynamic component={props.item.icon} class="h-5 w-5" />
      </span>
      {t(props.item.label)}
    </Link>
  );

  const SectionHeader = (props: { section: ReturnType<typeof sections>[number] }) => (
    <Show when={props.section.header}>
      {(header) => (
        <div class="mt-3 px-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">
          {t(header())}
        </div>
      )}
    </Show>
  );

  const Sidebar = (props: { slot: NavSlot }) => (
    <nav
      class="flex h-full flex-col gap-1.5 p-3"
      data-testid={slotTestId("space-sidebar", props.slot)}
    >
      <div class="mb-3 flex items-center justify-between px-2 py-1">
        <Link
          to="/learn"
          class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
          onClick={() => setMobileOpen(false)}
        >
          <Logo />
        </Link>
        {/* F3-C: campana de notificaciones (ya sufija sus testids por slot). */}
        <NotificationsBell slot={props.slot} />
      </div>

      {/* Secciones superiores (scrollables si desbordan). */}
      <div class="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto">
        <For each={sections().filter((s) => s.id !== "account")}>
          {(section) => (
            <>
              <SectionHeader section={section} />
              <For each={section.items}>{(item) => <NavLink item={item} slot={props.slot} />}</For>
            </>
          )}
        </For>
      </div>

      {/* Sección Cuenta — anclada abajo, encima del switcher. */}
      <For each={sections().filter((s) => s.id === "account")}>
        {(section) => (
          <>
            <SectionHeader section={section} />
            <For each={section.items}>{(item) => <NavLink item={item} slot={props.slot} />}</For>
          </>
        )}
      </For>

      {/* ── Account switcher (multi-account #18) ─────────────────────────────
          Trigger only: flips `?account=switch`; the overlay itself is mounted
          once at the layout level (AccountSwitcherHost, below). */}
      <button
        type="button"
        onClick={() => {
          setMobileOpen(false);
          openSwitcher();
        }}
        class="flex w-full items-center gap-2.5 rounded-2xl border-t border-border/40 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
        aria-label={t(msg`Switch account`)}
        data-testid={slotTestId("account-switcher-trigger", props.slot)}
      >
        <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
          <Show when={activeAccount()?.kind === "real"} fallback={<UserCircle class="h-4 w-4" />}>
            <span class="uppercase">{activeInitial()}</span>
          </Show>
        </span>
        <span class="min-w-0 flex-1 text-left">
          <span class="block truncate font-medium text-foreground">{activeLabel()}</span>
          <Show when={activeAccount()?.email}>
            <span class="block truncate text-xs text-muted-foreground/80">
              {activeAccount()?.email}
            </span>
          </Show>
        </span>
        <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
      </button>
    </nav>
  );

  return (
    <div class="flex h-[100dvh] w-full overflow-hidden bg-background text-foreground">
      {/* Sidebar de escritorio */}
      <aside class="hidden w-60 shrink-0 border-r border-border bg-surface/40 md:block">
        <Sidebar slot="desktop" />
      </aside>

      {/* Drawer móvil (mismo mecanismo que el canon: siempre montado, entra y
          sale con `transform`). Extraído para poder probarlo sin el shell.
          Su copia de la navegación sufija los testids (`slot="mobile"`) porque
          coexiste en el DOM con la de escritorio — ver `slotTestId`. */}
      <MobileNavDrawer isOpen={mobileOpen} setOpen={setMobileOpen} closeLabel={t(msg`Close`)}>
        <Sidebar slot="drawer" />
      </MobileNavDrawer>

      <div class="flex min-w-0 flex-1 flex-col">
        {/* Barra superior (móvil) */}
        <header class="flex items-center gap-2 border-b border-border p-2 md:hidden">
          <button
            type="button"
            data-testid="open-menu"
            onClick={() => setMobileOpen(true)}
            aria-label={t(msg`Menu`)}
            class="rounded-xl p-1.5 text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent"
          >
            <Menu class="h-6 w-6" />
          </button>
          <Logo />
          {/* F3-C: campana de notificaciones (barra móvil). */}
          <div class="ml-auto">
            <NotificationsBell slot="mobile" />
          </div>
        </header>

        {/* F2-C: barra persistente "Viendo como <name>" durante la impersonación. */}
        <ImpersonationBanner />

        {/* LF-B: banner offline global (modo avión) — el componente existía
            pero no estaba montado en el shell; sin él, trabajar sin red no
            tenía ninguna señal honesta fuera del pill del perfil. */}
        <OfflineBanner />

        <main class="min-h-0 flex-1 overflow-y-auto" data-testid="space-main">
          {/* §M2: enlace contextual pantalla → guía. Se monta UNA vez y deriva
              el área de la ruta activa, así que ninguna pantalla se queda sin
              ayuda por olvido; se oculta si el rol o un flag no la habilitan. */}
          <RouteHelpLink />
          {/* Entrada por ruta: al cambiar la clave se remonta este envoltorio y
              se vuelve a reproducir `slide-in-up` (solo transform + opacity).
              La clave es la ruta a secas, sin recortes: el canon le quita el
              sufijo del detalle porque allí ese detalle es un cajón que deja
              montada la página de debajo, y sin recortar la entrada del padre se
              re-dispararía al abrirlo. Aquí no aplica — las rutas `$id` de space
              son páginas completas y el único overlay direccionable va por
              search param (`?account=switch`), que no toca el pathname.
              `prefers-reduced-motion` lo gobierna el guard global. */}
          <Show when={location().pathname} keyed>
            <div class="flex min-h-0 flex-1 flex-col animate-slide-in-up will-change-[transform,opacity]">
              <Outlet />
            </div>
          </Show>
        </main>
      </div>

      {/* URL-driven account switcher overlay (`?account=switch`) — single mount. */}
      <AccountSwitcherHost />
    </div>
  );
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  // Multi-account: honor an explicit GUEST choice — don't verify with the server.
  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  const fetchSession = async () =>
    api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        const SESSION_CHECK_TIMEOUT = 1500;
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT);
      });
      return await Promise.race([
        api.GET("/api/auth/get-session", { params: { query: { jwt: true } } }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error o timeout al verificar sesión:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    return;
  }

  const { data, error, response } = sessionResult;

  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({ type: "SERVER_REPLY", session: sessionPayload, error: errorPayload });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      return;
    }
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/u/$u",
  params: {
    parse: (raw: Record<string, string>) => {
      const index = Number.parseInt(raw.u ?? "", 10);
      if (!Number.isInteger(index) || index < 0 || String(index) !== raw.u) {
        throw new Error(`Invalid account index: ${raw.u}`);
      }
      return { u: index };
    },
    stringify: (params: { u: number }) => ({ u: String(params.u) }),
  },
  validateSearch: validateAppLayoutSearch,
  beforeLoad: ({ params, location }) => {
    const accountsState = readAccountsStateSync();
    if (accountsState.accounts.length === 0) {
      void accountsStore.ensureGuestActive();
      if (params.u !== 0) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          to: buildUPath(0, stripUPrefix(location.pathname)),
          search: location.search as Record<string, unknown>,
          replace: true,
        });
      }
    } else {
      const target = outOfRangeRedirectIndex(accountsState, params.u);
      if (target !== null && target !== params.u) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          to: buildUPath(target, stripUPrefix(location.pathname)),
          search: location.search as Record<string, unknown>,
          replace: true,
        });
      }
    }

    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;
      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
  },

  component: SpaceShell,
});
